import type {
    MixRecipe,
    MixRecipeDueDiligence,
    MixRecipeProduct,
    RawMixRecipe,
    RawMixRecipeDueDiligence,
    RawMixRecipeProduct
} from './types';

// mix-recipe-api is still-settling demo backend. This helper defensively
// maps any snake_case fields we've seen in the raw payload into the
// camelCase MixRecipe contract components consume. When the backend ships
// only camelCase the mapping collapses into an identity copy — no runtime
// penalty.
const normalizeDueDiligence = (
    raw: RawMixRecipeDueDiligence | undefined
): MixRecipeDueDiligence => ({
    requirement: raw?.requirement ?? '',
    state: raw?.state ?? ''
});

const normalizeProduct = (raw: RawMixRecipeProduct): MixRecipeProduct => {
    const rawDueDiligences = raw.dueDiligences ?? raw.due_diligences ?? [];
    const rawUnlicensedStates = raw.unlicensedStates ?? raw.unlicensed_states;
    const isDicamba = raw.isDicamba ?? raw.is_dicamba;
    const isRestrictedUse = raw.isRestrictedUse ?? raw.is_restricted_use;

    const product: MixRecipeProduct = {
        epaRegNumber: raw.epaRegNumber ?? raw.epa_reg_number ?? '',
        productId: raw.productId ?? raw.product_id ?? '',
        productName: raw.productName ?? raw.product_name ?? '',
        rate: raw.rate ?? 0,
        rateUnit: raw.rateUnit ?? raw.rate_unit ?? 'oz/ac'
    };

    if (isDicamba !== undefined) {
        product.isDicamba = isDicamba;
    }

    if (isRestrictedUse !== undefined) {
        product.isRestrictedUse = isRestrictedUse;
    }

    if (rawUnlicensedStates !== undefined) {
        product.unlicensedStates = rawUnlicensedStates;
    }

    if (rawDueDiligences.length > 0) {
        product.dueDiligences = rawDueDiligences.map(normalizeDueDiligence);
    }

    return product;
};

const normalizeMixRecipe = (raw: RawMixRecipe | null): MixRecipe | null => {
    if (raw === null) {
        return null;
    }

    return {
        mixRecipeId: raw.mixRecipeId ?? raw.mix_recipe_id ?? '',
        name: raw.name ?? '',
        products: (raw.products ?? []).map(normalizeProduct)
    };
};

export default normalizeMixRecipe;
